import React, { useMemo } from 'react';
import tw, { styled, css } from 'twin.macro';
import { CJAtelier } from 'types/cj';
import { EleveGroup } from 'types/eleveGroups';
import { GroupTag } from 'containers/CahierJournal/components/GroupTag';
import isPropValid from '@emotion/is-prop-valid';
import { areAteliersEmpty } from 'containers/CahierJournal/EventModal/ModalBody/Ateliers/areAteliersEmpty';
import { generateTimeSlots } from './generateTimeSlots';

interface Props {
  ateliers: CJAtelier[];
  groups: EleveGroup[];
  condensed?: boolean;
  startTime: Date;
  endTime: Date;
}

interface TableContainerProps {
  children: React.ReactNode;
  condensed?: boolean;
  ateliers: CJAtelier[]; // Replace with your actual type
}

const StyledTableContainer = styled('div', {
  shouldForwardProp: prop => isPropValid(prop),
})<TableContainerProps>(({ condensed = false, ateliers = [] }) => [
  tw`overflow-x-auto`,

  !condensed && tw`rounded-xl border border-neutral-quiet`,

  css`
    & table {
      table-layout: auto;
      border-collapse: collapse;
      width: 100%;

      ${condensed && 'border: none;'}
    }

    & th:first-child,
    & td:first-child {
      width: 1%;
      white-space: nowrap;
    }

    & .atelier-column {
      width: ${ateliers.length
        ? `${Math.floor(100 / ateliers.length)}%`
        : '100%'};
    }

    & thead {
      ${condensed ? tw`bg-transparent` : tw`bg-neutral-supershy`}
    }

    & td,
    & th {
      ${tw`border border-neutral-quiet`}
    }

    & tr:first-child th {
      border-top: none;
    }

    & tr:last-child td {
      border-bottom: none;
    }

    & tr th:first-child,
    & tr td:first-child {
      border-left: none;
    }

    & tr th:last-child,
    & tr td:last-child {
      border-right: none;
    }
  `,
]);

const Table = tw.table`w-full`;
const TimeColumnTh = tw.th`px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap`;
const AtelierTh = tw.th`px-2 py-1 text-left text-xs font-medium tracking-wider`;
const Thead = tw.thead``;
const Tbody = tw.tbody`bg-white divide-y divide-gray-200`;
const Td = tw.td`px-2 py-1.5 text-sm text-gray-900`;
const TimeCell = tw.td`px-2 py-1.5 whitespace-nowrap text-sm font-medium text-gray-900`;

function formatTime(date: Date): string {
  return date.toTimeString().slice(0, 5);
}

// La position sur la table de jointure atelier_groups détermine en fait la position dans le tableau des slices.
export function RotateInSeanceTable(props: Props) {
  const { ateliers, groups, startTime, endTime, condensed } = props;

  /**
   * Determine the highest groupPivot.position across all ateliers.
   * Because you said positions are 1-based, a group pivot with position=0
   * is invalid data and won't appear. So we only consider pivot.position >= 1.
   */
  const maxPosition = useMemo(() => {
    let maxPos = 0;
    for (const atelier of ateliers) {
      for (const gp of atelier.groupPivots || []) {
        if (gp.position > maxPos) {
          maxPos = gp.position;
        }
      }
    }
    return maxPos;
  }, [ateliers]);

  const ateliersEmpty = areAteliersEmpty(ateliers);

  const sliceCount = maxPosition; // If maxPosition=0 => no rows

  const timeSlots = useMemo(
    () => generateTimeSlots(startTime, endTime, sliceCount),
    [startTime, endTime, sliceCount],
  );

  if (sliceCount === 0) {
    return null;
  }

  return (
    <StyledTableContainer condensed={condensed} ateliers={ateliers}>
      <Table data-testid="rotation-table">
        <Thead>
          <tr>
            <TimeColumnTh></TimeColumnTh>
            {ateliers.map(atelier => (
              <AtelierTh key={atelier.id} className="atelier-column">
                <div>{atelier.title}</div>
                {ateliersEmpty && (
                  <div tw="text-muted text-xs italic">
                    ({atelier.supervision})
                  </div>
                )}
              </AtelierTh>
            ))}
          </tr>
        </Thead>
        <Tbody>
          {Array.from({ length: sliceCount }, (_, idx) => idx + 1).map(
            sliceNumber => {
              const slotIndex = sliceNumber - 1;
              const slot = timeSlots[slotIndex];
              // If we couldn't generate that slot (time range too short),
              // we can either skip or fill with a placeholder time
              const startLabel = slot
                ? formatTime(slot.startTime)
                : `Slice #${sliceNumber}`;
              return (
                <tr key={sliceNumber}>
                  <TimeCell>{startLabel}</TimeCell>
                  {ateliers.map(atelier => {
                    // For each atelier, show all groupPivot with position === sliceNumber
                    const relevant = (atelier.groupPivots || []).filter(
                      gp => gp.position === sliceNumber,
                    );
                    if (relevant.length === 0) {
                      return (
                        <Td key={atelier.id} className="atelier-column">
                          -
                        </Td>
                      );
                    }
                    return (
                      <Td key={atelier.id} tw="py-2" className="atelier-column">
                        <div tw="flex flex-wrap gap-1">
                          {relevant.map(pivot => {
                            const group = groups.find(g => g.id === pivot.id);
                            if (!group) {
                              return <div key={pivot.id} />;
                            }
                            return (
                              <GroupTag
                                data-testid="group-tag"
                                withoutIcon={condensed}
                                key={pivot.id}
                                color={group.color}
                                groupName={group.name}
                                isEphemeral={group.isEphemeral}
                                groupEleves={group.eleves}
                              />
                            );
                          })}
                        </div>
                      </Td>
                    );
                  })}
                </tr>
              );
            },
          )}
        </Tbody>
      </Table>
    </StyledTableContainer>
  );
}
